import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { getIndexerDerivativesApi } from '@shared/service/derivative'
import { combineOrderbookRecords } from '@/app/utils/market'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import type { DerivativeTrade } from '@injectivelabs/sdk-ts'
import type { SharedUiOrderbookWithSequence } from '@shared/types'
import type { TradeExecutionSide } from '@injectivelabs/sdk-ts/types/light'

type DerivativeMarketDataStoreState = {
  trades: DerivativeTrade[]
  orderbook?: SharedUiOrderbookWithSequence
}

const initialStateFactory = (): DerivativeMarketDataStoreState => ({
  trades: markRaw([] as DerivativeTrade[]),
  orderbook: undefined
})

export const useDerivativeMarketDataStore = defineStore('derivativeMarketData', {
  state: (): DerivativeMarketDataStoreState => initialStateFactory(),
  getters: {
    buys: (state) => {
      return state.orderbook?.buys || []
    },

    sells: (state) => {
      return state.orderbook?.sells || []
    }
  },
  actions: {
    setOrderbook(orderbook?: SharedUiOrderbookWithSequence) {
      this.orderbook = orderbook
        ? markRaw({
            ...orderbook,
            buys: markRaw(orderbook.buys),
            sells: markRaw(orderbook.sells)
          })
        : undefined
    },

    setTrades(trades: DerivativeTrade[]) {
      this.trades = markRaw(trades)
    },

    async fetchOrderbook(marketId: string) {
      const derivativeMarketDataStore = useDerivativeMarketDataStore()
      const indexerDerivativesApi = await getIndexerDerivativesApi()
      const currentOrderbookSequence =
        derivativeMarketDataStore.orderbook?.sequence || 0
      const latestOrderbook =
        await indexerDerivativesApi.fetchOrderbookV2(marketId)
      const latestOrderbookIsMostRecent =
        latestOrderbook.sequence >= currentOrderbookSequence

      if (latestOrderbookIsMostRecent) {
        derivativeMarketDataStore.setOrderbook(latestOrderbook)
      }

      // handle race condition between fetch and stream
      derivativeMarketDataStore.setOrderbook({
        sequence: latestOrderbookIsMostRecent
          ? latestOrderbook.sequence
          : currentOrderbookSequence,
        buys: combineOrderbookRecords({
          isBuy: true,
          currentRecords: latestOrderbook.buys,
          updatedRecords: derivativeMarketDataStore.orderbook?.buys
        }),
        sells: combineOrderbookRecords({
          isBuy: false,
          currentRecords: latestOrderbook.sells,
          updatedRecords: derivativeMarketDataStore.orderbook?.sells
        })
      })
    },

    async fetchTrades({
      marketId,
      executionSide
    }: {
      marketId: string
      executionSide?: TradeExecutionSide
    }) {
      const indexerDerivativesApi = await getIndexerDerivativesApi()

      const { trades } = await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.marketTrades, {
          marketId,
          executionSide
        }),
        () =>
          indexerDerivativesApi.fetchTrades({
            executionSide,
            marketIds: [marketId]
          })
      )

      useDerivativeMarketDataStore().setTrades(trades)
    },

    resetOrderbookAndTrades() {
      const derivativeMarketDataStore = useDerivativeMarketDataStore()

      derivativeMarketDataStore.setTrades([])
      derivativeMarketDataStore.setOrderbook()
    },

    reset() {
      const derivativeMarketDataStore = useDerivativeMarketDataStore()
      const { trades, orderbook } = initialStateFactory()

      derivativeMarketDataStore.setTrades(trades)
      derivativeMarketDataStore.setOrderbook(orderbook)
    }
  }
})
